import React, { useState, useEffect } from 'react'
import {
  Wallet,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  Shield,
  Info,
  X,
  ChevronDown
} from 'lucide-react'
import { 
  walletConnectionManager, 
  type WalletType, 
  type NetworkInfo, 
  type WalletConnectionState,
  type ValidationResult
} from '../services/walletConnectionManager'

interface WalletConnectionFormProps {
  onConnectionSuccess?: (walletAddress: string, networkId: string) => void
  onConnectionError?: (error: string) => void
  className?: string
  showNetworkSelection?: boolean
  preferredNetwork?: string
}

interface FormState {
  selectedWallet: string
  selectedNetwork: string
  showWalletDropdown: boolean
  showNetworkDropdown: boolean
}

interface ValidationState {
  walletValidation: ValidationResult | null
  networkValidation: ValidationResult | null
  showValidation: boolean
}

export const WalletConnectionForm: React.FC<WalletConnectionFormProps> = ({
  onConnectionSuccess,
  onConnectionError,
  className = '',
  showNetworkSelection = true,
  preferredNetwork = 'ethereum'
}) => {
  // Form state
  const [formState, setFormState] = useState<FormState>({
    selectedWallet: '',
    selectedNetwork: preferredNetwork,
    showWalletDropdown: false,
    showNetworkDropdown: false
  })

  // Validation state
  const [validationState, setValidationState] = useState<ValidationState>({
    walletValidation: null,
    networkValidation: null,
    showValidation: false
  })

  // Connection state from manager
  const [connectionState, setConnectionState] = useState<WalletConnectionState>(
    walletConnectionManager.getState()
  )

  // Available options
  const [supportedWallets] = useState<WalletType[]>(walletConnectionManager.getSupportedWallets())
  const [supportedNetworks] = useState<NetworkInfo[]>(walletConnectionManager.getSupportedNetworks())
  const [compatibleNetworks, setCompatibleNetworks] = useState<NetworkInfo[]>([])

  // Subscribe to connection state changes
  useEffect(() => {
    const unsubscribe = walletConnectionManager.subscribe(setConnectionState)
    return unsubscribe
  }, [])

  // Update compatible networks when wallet selection changes
  useEffect(() => {
    if (formState.selectedWallet) {
      const compatible = walletConnectionManager.getWalletSupportedNetworks(formState.selectedWallet)
      setCompatibleNetworks(compatible)
      
      // Auto-select first compatible network if current selection is not compatible
      if (compatible.length > 0 && !compatible.find(n => n.id === formState.selectedNetwork)) {
        setFormState(prev => ({
          ...prev,
          selectedNetwork: compatible[0].id
        }))
      }
    } else {
      setCompatibleNetworks(supportedNetworks)
    }
  }, [formState.selectedWallet, supportedNetworks])

  // Validate form inputs in real-time
  useEffect(() => {
    if (validationState.showValidation) {
      validateForm()
    }
  }, [formState.selectedWallet, formState.selectedNetwork, validationState.showValidation])

  const validateForm = () => {
    const walletValidation = walletConnectionManager.validateWalletType(formState.selectedWallet)
    const networkValidation = walletConnectionManager.validateNetworkCompatibility(
      formState.selectedWallet, 
      formState.selectedNetwork
    )

    setValidationState(prev => ({
      ...prev,
      walletValidation,
      networkValidation
    }))
  }

  const handleWalletSelect = (walletId: string) => {
    setFormState(prev => ({
      ...prev,
      selectedWallet: walletId,
      showWalletDropdown: false
    }))

    // Clear previous validation errors
    if (validationState.showValidation) {
      validateForm()
    }
  }

  const handleNetworkSelect = (networkId: string) => {
    setFormState(prev => ({
      ...prev,
      selectedNetwork: networkId,
      showNetworkDropdown: false
    }))

    // Clear previous validation errors
    if (validationState.showValidation) {
      validateForm()
    }
  }

  const handleConnect = async () => {
    // Enable validation display
    setValidationState(prev => ({ ...prev, showValidation: true }))
    
    // Validate form
    const walletValidation = walletConnectionManager.validateWalletType(formState.selectedWallet)
    const networkValidation = walletConnectionManager.validateNetworkCompatibility(
      formState.selectedWallet, 
      formState.selectedNetwork
    )

    setValidationState(prev => ({
      ...prev,
      walletValidation,
      networkValidation
    }))

    // Check if validation passed
    if (!walletValidation.isValid || !networkValidation.isValid) {
      return
    }

    try {
      const result = await walletConnectionManager.connectWallet(
        formState.selectedWallet,
        formState.selectedNetwork
      )

      if (result.success && result.walletAddress) {
        onConnectionSuccess?.(result.walletAddress, formState.selectedNetwork)
      } else {
        const errorMessage = result.error || 'Connection failed'
        onConnectionError?.(errorMessage)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unexpected error occurred'
      onConnectionError?.(errorMessage)
    }
  }

  const handleDisconnect = async () => {
    await walletConnectionManager.disconnectWallet()
  }

  const handleRetry = async () => {
    // Clear validation errors
    setValidationState(prev => ({
      ...prev,
      walletValidation: null,
      networkValidation: null,
      showValidation: false
    }))

    // Clear error state
    walletConnectionManager.clearError()

    // Try to retry the connection if possible
    if (connectionState.canRetry) {
      try {
        const result = await walletConnectionManager.retryConnection()
        if (result) {
          if (result.success && result.walletAddress) {
            onConnectionSuccess?.(result.walletAddress, formState.selectedNetwork)
          } else {
            const errorMessage = result.error || 'Retry failed'
            onConnectionError?.(errorMessage)
          }
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Retry failed'
        onConnectionError?.(errorMessage)
      }
    }
  }

  const getSelectedWallet = () => {
    return supportedWallets.find(w => w.id === formState.selectedWallet)
  }

  const getSelectedNetwork = () => {
    return supportedNetworks.find(n => n.id === formState.selectedNetwork)
  }

  const hasValidationErrors = () => {
    return validationState.showValidation && (
      (validationState.walletValidation && !validationState.walletValidation.isValid) ||
      (validationState.networkValidation && !validationState.networkValidation.isValid)
    )
  }

  // If already connected, show connected state
  if (connectionState.isConnected) {
    return (
      <div className={`wallet-connection-form ${className}`}>
        <div className="professional-card">
          <div className="professional-card__header">
            <div className="flex items-center gap-3">
              <div className="professional-demo__feature-icon professional-demo__feature-icon--green">
                <CheckCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="professional-card__title">Wallet Connected</h3>
                <p className="professional-card__subtitle">Your wallet is successfully connected</p>
              </div>
            </div>
          </div>
          <div className="professional-card__content">
            <div className="wallet-connection-form__connected-info">
              <div className="wallet-connection-form__connected-row">
                <span className="wallet-connection-form__connected-label">Wallet:</span>
                <span className="wallet-connection-form__connected-value">
                  {supportedWallets.find(w => w.id === connectionState.walletType)?.name || connectionState.walletType}
                </span>
              </div>
              <div className="wallet-connection-form__connected-row">
                <span className="wallet-connection-form__connected-label">Network:</span>
                <span className="wallet-connection-form__connected-value">
                  {connectionState.networkName}
                </span>
              </div>
              <div className="wallet-connection-form__connected-row">
                <span className="wallet-connection-form__connected-label">Address:</span>
                <span className="wallet-connection-form__connected-value wallet-connection-form__address">
                  {connectionState.walletAddress}
                </span>
              </div>
              {connectionState.lastConnected && (
                <div className="wallet-connection-form__connected-row">
                  <span className="wallet-connection-form__connected-label">Connected:</span>
                  <span className="wallet-connection-form__connected-value">
                    {connectionState.lastConnected.toLocaleTimeString()}
                  </span>
                </div>
              )}
            </div>
            
            <div className="wallet-connection-form__actions">
              <button
                onClick={handleDisconnect}
                className="professional-btn professional-btn--secondary"
              >
                <X className="w-4 h-4" />
                Disconnect Wallet
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`wallet-connection-form ${className}`}>
      <div className="professional-card">
        <div className="professional-card__header">
          <div className="flex items-center gap-3">
            <div className="professional-demo__feature-icon professional-demo__feature-icon--blue">
              <Wallet className="w-5 h-5" />
            </div>
            <div>
              <h3 className="professional-card__title">Connect Your Wallet</h3>
              <p className="professional-card__subtitle">
                Connect your wallet to pay with existing stablecoins
              </p>
            </div>
          </div>
        </div>
        <div className="professional-card__content">
          {/* Wallet Type Selection */}
          <div className="wallet-connection-form__field">
            <label className="professional-form-label">
              Select Wallet Type
              <span className="wallet-connection-form__required">*</span>
            </label>
            
            <div className="wallet-connection-form__dropdown">
              <button
                type="button"
                onClick={() => setFormState(prev => ({ 
                  ...prev, 
                  showWalletDropdown: !prev.showWalletDropdown,
                  showNetworkDropdown: false
                }))}
                className={`wallet-connection-form__dropdown-trigger ${
                  hasValidationErrors() && validationState.walletValidation && !validationState.walletValidation.isValid
                    ? 'wallet-connection-form__dropdown-trigger--error' : ''
                }`}
              >
                <div className="wallet-connection-form__dropdown-content">
                  {formState.selectedWallet ? (
                    <div className="wallet-connection-form__selected-option">
                      <span className="wallet-connection-form__wallet-icon">
                        {getSelectedWallet()?.icon}
                      </span>
                      <div className="wallet-connection-form__wallet-info">
                        <div className="wallet-connection-form__wallet-name">
                          {getSelectedWallet()?.name}
                        </div>
                        <div className="wallet-connection-form__wallet-description">
                          {getSelectedWallet()?.description}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="wallet-connection-form__placeholder">Choose a wallet...</span>
                  )}
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${
                  formState.showWalletDropdown ? 'rotate-180' : ''
                }`} />
              </button>

              {formState.showWalletDropdown && (
                <div className="wallet-connection-form__dropdown-menu">
                  {supportedWallets.map((wallet) => (
                    <button
                      key={wallet.id}
                      type="button"
                      onClick={() => handleWalletSelect(wallet.id)}
                      disabled={!wallet.isAvailable}
                      className={`wallet-connection-form__dropdown-option ${
                        !wallet.isAvailable ? 'wallet-connection-form__dropdown-option--disabled' : ''
                      }`}
                    >
                      <span className="wallet-connection-form__wallet-icon">
                        {wallet.icon}
                      </span>
                      <div className="wallet-connection-form__wallet-info">
                        <div className="wallet-connection-form__wallet-name">
                          {wallet.name}
                        </div>
                        <div className="wallet-connection-form__wallet-description">
                          {wallet.description}
                        </div>
                      </div>
                      {!wallet.isAvailable && (
                        <span className="wallet-connection-form__unavailable">Unavailable</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Wallet validation errors */}
            {validationState.showValidation && validationState.walletValidation && !validationState.walletValidation.isValid && (
              <div className="wallet-connection-form__validation-errors">
                {validationState.walletValidation.errors.map((error, index) => (
                  <div key={index} className="wallet-connection-form__validation-error">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Network Selection */}
          {showNetworkSelection && (
            <div className="wallet-connection-form__field">
              <label className="professional-form-label">
                Select Network
                <span className="wallet-connection-form__required">*</span>
              </label>
              
              <div className="wallet-connection-form__dropdown">
                <button
                  type="button"
                  onClick={() => setFormState(prev => ({ 
                    ...prev, 
                    showNetworkDropdown: !prev.showNetworkDropdown,
                    showWalletDropdown: false
                  }))}
                  disabled={!formState.selectedWallet}
                  className={`wallet-connection-form__dropdown-trigger ${
                    hasValidationErrors() && validationState.networkValidation && !validationState.networkValidation.isValid
                      ? 'wallet-connection-form__dropdown-trigger--error' : ''
                  } ${!formState.selectedWallet ? 'wallet-connection-form__dropdown-trigger--disabled' : ''}`}
                >
                  <div className="wallet-connection-form__dropdown-content">
                    {formState.selectedNetwork && getSelectedNetwork() ? (
                      <div className="wallet-connection-form__selected-option">
                        <div className="wallet-connection-form__network-info">
                          <div className="wallet-connection-form__network-name">
                            {getSelectedNetwork()?.name}
                          </div>
                          <div className="wallet-connection-form__network-description">
                            Chain ID: {getSelectedNetwork()?.chainId}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="wallet-connection-form__placeholder">
                        {formState.selectedWallet ? 'Choose a network...' : 'Select wallet first'}
                      </span>
                    )}
                  </div>
                  <ChevronDown className={`w-4 h-4 transition-transform ${
                    formState.showNetworkDropdown ? 'rotate-180' : ''
                  }`} />
                </button>

                {formState.showNetworkDropdown && formState.selectedWallet && (
                  <div className="wallet-connection-form__dropdown-menu">
                    {compatibleNetworks.map((network) => (
                      <button
                        key={network.id}
                        type="button"
                        onClick={() => handleNetworkSelect(network.id)}
                        className="wallet-connection-form__dropdown-option"
                      >
                        <div className="wallet-connection-form__network-info">
                          <div className="wallet-connection-form__network-name">
                            {network.name}
                          </div>
                          <div className="wallet-connection-form__network-description">
                            Chain ID: {network.chainId} • {network.nativeCurrency.symbol}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Network validation errors */}
              {validationState.showValidation && validationState.networkValidation && !validationState.networkValidation.isValid && (
                <div className="wallet-connection-form__validation-errors">
                  {validationState.networkValidation.errors.map((error, index) => (
                    <div key={index} className="wallet-connection-form__validation-error">
                      <AlertCircle className="w-4 h-4" />
                      <span>{error}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Network compatibility info */}
              {formState.selectedWallet && compatibleNetworks.length > 0 && (
                <div className="wallet-connection-form__network-info">
                  <Info className="w-4 h-4 text-blue-600" />
                  <span>
                    {getSelectedWallet()?.name} supports {compatibleNetworks.length} network
                    {compatibleNetworks.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Connection Error Display */}
          {connectionState.error && (
            <div className="wallet-connection-form__error">
              <AlertCircle className="w-5 h-5" />
              <div className="wallet-connection-form__error-content">
                <div className="wallet-connection-form__error-message">
                  {connectionState.error}
                </div>
                <div className="wallet-connection-form__error-guidance">
                  {walletConnectionManager.getConnectionErrorGuidance(connectionState.errorCode || undefined)}
                </div>
                
                {/* Detailed recovery steps */}
                <div className="wallet-connection-form__error-steps">
                  <div className="wallet-connection-form__error-steps-title">
                    Recovery Steps:
                  </div>
                  <ul className="wallet-connection-form__error-steps-list">
                    {walletConnectionManager.getDetailedErrorRecovery(connectionState.errorCode || undefined).map((step, index) => (
                      <li key={index} className="wallet-connection-form__error-step">
                        {index + 1}. {step}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Retry information */}
                {connectionState.retryCount > 0 && (
                  <div className="wallet-connection-form__retry-info">
                    Retry attempt: {connectionState.retryCount}/3
                  </div>
                )}
              </div>
              
              {/* Retry button */}
              {connectionState.canRetry && (
                <button
                  onClick={handleRetry}
                  disabled={connectionState.isConnecting}
                  className="wallet-connection-form__error-retry"
                >
                  {connectionState.isConnecting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  {connectionState.isConnecting ? 'Retrying...' : 'Retry'}
                </button>
              )}
            </div>
          )}

          {/* Security Notice */}
          <div className="wallet-connection-form__security-notice">
            <Shield className="w-4 h-4 text-green-600" />
            <div className="wallet-connection-form__security-content">
              <div className="wallet-connection-form__security-title">Secure Connection</div>
              <div className="wallet-connection-form__security-description">
                Your wallet connection is secure and encrypted. We never store your private keys.
              </div>
            </div>
          </div>

          {/* Connect Button */}
          <div className="wallet-connection-form__actions">
            <button
              onClick={handleConnect}
              disabled={connectionState.isConnecting || !formState.selectedWallet || (!formState.selectedNetwork && showNetworkSelection)}
              className="professional-btn professional-btn--primary wallet-connection-form__connect-btn"
            >
              {connectionState.isConnecting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Wallet className="w-4 h-4" />
                  Connect Wallet
                </>
              )}
            </button>

            <div className="wallet-connection-form__help-text">
              <Info className="w-4 h-4 text-blue-600" />
              <span>
                Make sure your wallet is unlocked and set to the correct network
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}